import { LocalDate } from '@js-joda/core'
import { Distance, EstimateMass, EstimateMassUnit, Mass, MassUnit } from '@lune-climate/lune'
import { Distance as DapiDistance } from '@lune-fe/dapi-models'
import { countriesList } from '@lune-fe/lune-ui-lib'
import { Big } from 'big.js'
import getUserLocale from 'get-user-locale'
import moment from 'moment'
import { None, Option, Some } from 'ts-results-es'

/**
 * The function's only purpose is to fail at compile time if the parameter passed is *not*
 * of the type specified.
 *
 * This is useful to ensure, at compile time, what type are we dealing with. Particularly
 * helpful in making sure we handle all possible values in some context.
 *
 * @example
 *
 * ```
 * function f(value: 'a' | 'b') {
 *     if (value === 'a') {
 *         console.log('We got a')
 *     }
 *     else {
 *         assertType<'b'>(value)
 *         console.log('We got b')
 *     }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function assertType<T>(_value: T) {}

export function optionFromNullable<T>(value: T | null): Option<T> {
    if (value === null) {
        return None
    }
    return Some(value)
}

export const formatDate = (date: string): string => {
    return moment(date).format('ll')
}

export const formatNumbers = (number: number | string, decimals?: number) => {
    const userLocale = getUserLocale() || 'en-GB'
    let numberFormat

    if (decimals) {
        number = parseFloat(number.toString()).toFixed(decimals)
    }

    try {
        numberFormat = new Intl.NumberFormat(userLocale, { maximumFractionDigits: decimals || 3 })
    } catch (e: any) {
        // ^ No type safety in this block, we can in principle receive anything and assume things.

        console.warn(
            `${e.name} was caught while constructing Intl.NumberFormat.
                      Retrying with omitted currencyDisplay: 'narrowSymbol' (known not to be supported on some Safari versions...`,
        )
        // Fallback config omitting currencyDisplay: 'narrowSymbol',
        // Mainly needed for Safari 10-14
        // Details https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_currencydisplay
        numberFormat = new Intl.NumberFormat(userLocale, { maximumFractionDigits: decimals || 3 })
    }

    try {
        return numberFormat.format(Big(number).toNumber())
    } catch (error) {
        console.error('Error formatting number:', error)
    }

    return number
}

/**
 * Strict numeric formatter for decimal integer and floating-point values.
 *
 * Invalid or missing input returns the provided fallback instead of echoing the raw value.
 */
export const formatNumericValue = (
    value?: string | number | null,
    decimals = 0,
    fallback = '-',
): string => {
    if (value === null || value === undefined || value === '') {
        return fallback
    }

    const numericValue = parseBigNumericValue(value)

    if (!numericValue) {
        return fallback
    }

    const safeDecimals = numericValue.eq(numericValue.round(0, 0)) ? 0 : decimals
    const minimumVisibleValue = Big(1).div(Big(10).pow(safeDecimals || 1))
    const absoluteValue = numericValue.abs()

    const displayValue =
        safeDecimals > 0 && absoluteValue.gt(0) && absoluteValue.lt(minimumVisibleValue)
            ? numericValue.gt(0)
                ? minimumVisibleValue.toNumber()
                : minimumVisibleValue.mul(-1).toNumber()
            : numericValue.toNumber()

    return new Intl.NumberFormat(getUserLocale() || 'en-GB', {
        maximumFractionDigits: safeDecimals,
        minimumFractionDigits: safeDecimals,
    }).format(displayValue)
}

/**
 * Returns `null` when `Big` cannot parse the input, e.g. invalid decimal strings or NaN/Infinity.
 */
const parseBigNumericValue = (value: string | number | Big): Big | null => {
    if (value instanceof Big) {
        return value
    }

    try {
        return Big(value)
    } catch {
        return null
    }
}

export const getDisplayFractionDigits = (value: string | number | Big): number => {
    const numericValue = parseBigNumericValue(value)

    if (!numericValue) {
        return 0
    }

    const absoluteValue = numericValue.abs()

    if (absoluteValue.eq(0)) {
        return 0
    }

    if (absoluteValue.lt('0.001')) {
        return 6
    }

    if (absoluteValue.lt('1')) {
        return 3
    }

    return 2
}

/**
 * Adaptive formatter for numeric-looking values.
 *
 * Non-numeric strings are returned unchanged so existing label-like inputs keep rendering,
 * while invalid numeric primitives still fall back.
 */
export const formatAdaptiveNumericValue = (
    value?: string | number | Big | null,
    fallback = '-',
): string => {
    if (value === null || value === undefined || value === '') {
        return fallback
    }

    if (typeof value === 'number' && !Number.isFinite(value)) {
        return fallback
    }

    const parsedValue = parseBigNumericValue(value)
    const normalizedValue = `${value}`

    if (!parsedValue) {
        return normalizedValue
    }

    return formatNumericValue(
        parsedValue.toString(),
        getDisplayFractionDigits(parsedValue),
        fallback,
    )
}

export const KG_PER_LB = '0.45359237'

// Units getFormattedAmount can pick as a display unit: never lbs.
export type DisplayMassUnit = Exclude<MassUnit, MassUnit.LBS>

export type MassWithSupportedUnit = {
    amount: string
    unit: EstimateMassUnit | MassUnit | 'mg' | 'g' | 'kg' | 't' | 'lbs'
}

export const convertToTonne = (mass: MassWithSupportedUnit): Big => {
    if (mass.unit === EstimateMassUnit.T || mass.unit === MassUnit.T) {
        return Big(mass.amount)
    } else if (mass.unit === EstimateMassUnit.KG || mass.unit === MassUnit.KG) {
        return Big(mass.amount).div(1000)
    } else if (mass.unit === EstimateMassUnit.G || mass.unit === MassUnit.G) {
        return Big(mass.amount).div(1000000)
    } else if (mass.unit === EstimateMassUnit.LBS || mass.unit === MassUnit.LBS) {
        return Big(mass.amount).mul(KG_PER_LB).div(1000)
    } else {
        return Big(mass.amount).div(1000000000)
    }
}

export const convertToKg = (mass: EstimateMass | Mass) => {
    if (mass.unit === EstimateMassUnit.KG || mass.unit === MassUnit.KG) {
        return Big(mass.amount)
    } else if (mass.unit === EstimateMassUnit.G || mass.unit === MassUnit.G) {
        return Big(mass.amount).div(1000)
    } else if (mass.unit === EstimateMassUnit.T || mass.unit === MassUnit.T) {
        return Big(mass.amount).mul(1000)
    } else if (mass.unit === EstimateMassUnit.LBS || mass.unit === MassUnit.LBS) {
        return Big(mass.amount).mul(KG_PER_LB)
    } else {
        assertType<EstimateMassUnit.MG>(mass.unit)
        return Big(mass.amount).div(1000000)
    }
}

export const convertToGram = (mass: EstimateMass | Mass) => {
    if (mass.unit === EstimateMassUnit.G || mass.unit === MassUnit.G) {
        return Big(mass.amount)
    } else if (mass.unit === EstimateMassUnit.KG || mass.unit === MassUnit.KG) {
        return Big(mass.amount).mul(1000)
    } else if (mass.unit === EstimateMassUnit.T || mass.unit === MassUnit.T) {
        return Big(mass.amount).mul(1000000)
    } else if (mass.unit === EstimateMassUnit.LBS || mass.unit === MassUnit.LBS) {
        return Big(mass.amount).mul(KG_PER_LB).mul(1000)
    } else {
        assertType<EstimateMassUnit.MG>(mass.unit)
        return Big(mass.amount).div(1000)
    }
}

/**
 * Accepts a three-letter country code and returns the full country name from a predefined list of countries.
 * Optionally, you can pass a callback function to be called if the country code is not found.
 */
export const getCountryNameByThreeLetterCode = (
    code: string,
    onFail?: () => void,
): string | undefined => {
    const country = countriesList.find((c) => c[2] === code)?.[0]

    if (!country && onFail) {
        onFail()
    }

    return country
}

/**
 * Takes a stringified number and converts it to a standardized format in terms of grams (G), kilograms (KG), or tons (T).
 *
 * @param {string} amount - The stringified number to be formatted.
 *
 * If the number is less than 1 (but not 0 or empty), it will be converted to kilograms if the number is greater than or equal to 0.001, or grams if it's less than 0.001.
 * If the number is greater than or equal to 1, it will be converted to tons.
 * If the input is 0, empty, or null, it will return 0 grams.
 *
 * @returns {Object} An object containing the formatted number and the mass unit. The object has the following properties:
 * - amountAsNumber: The formatted number
 * - amountUnit: The unit of mass, which can be 'G', 'KG', or 'T'
 * - displayAmount: A locale-formatted string rounded for UI display
 */
const formatDisplayAmount = (amount: number): string => {
    return formatAdaptiveNumericValue(amount, '0')
}

export const getFormattedAmount = (
    amount: string,
): {
    amountAsNumber: number
    amountUnit: DisplayMassUnit
    displayAmount: string
} => {
    const amountAsNumber = Big(amount || 0)
    if (amountAsNumber.lt(1)) {
        const amountInKilos = amountAsNumber.mul(1000).toNumber()
        if (amountInKilos < 1) {
            const amountInGrams = Big(amountInKilos).mul(1000).toNumber()

            return {
                amountAsNumber: amountInGrams,
                amountUnit: MassUnit.G,
                displayAmount: formatDisplayAmount(amountInGrams),
            }
        } else {
            const amountInKilograms = amountAsNumber.mul(1000).toNumber()

            return {
                amountAsNumber: amountInKilograms,
                amountUnit: MassUnit.KG,
                displayAmount: formatDisplayAmount(amountInKilograms),
            }
        }
    } else {
        const amountInTonnes = amountAsNumber.toNumber()

        return {
            amountAsNumber: amountInTonnes,
            amountUnit: MassUnit.T,
            displayAmount: formatDisplayAmount(amountInTonnes),
        }
    }
}

export function getDistanceToKM(distance: Distance | DapiDistance): Big {
    switch (distance.unit) {
        case Distance.unit.KM:
        case 'km':
            return Big(distance.amount)
        case Distance.unit.NM:
        case 'nm':
            return Big(distance.amount).mul(1.852)
        case Distance.unit.MI:
        case 'mi':
            return Big(distance.amount).mul(1.60934)
    }
}

export const toTitleCase = (s: string) => {
    return s[0].toUpperCase() + s.slice(1)
}

export const formatToCurrency = (number: number | string, currency: string) => {
    if (!currency) {
        return number.toString()
    }

    const userLocale = getUserLocale() || 'en-GB'
    let numberFormat

    try {
        numberFormat = new Intl.NumberFormat(userLocale, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
        })
    } catch (e: any) {
        // ^ No type safety in this block, we can in principle receive anything and assume things.

        console.warn(
            `${e.name} was caught while constructing Intl.NumberFormat.
                      Retrying with omitted currencyDisplay: 'narrowSymbol' (known not to be supported on some Safari versions...`,
        )
        // Fallback config omitting currencyDisplay: 'narrowSymbol',
        // Mainly needed for Safari 10-14
        // Details https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_currencydisplay
        numberFormat = new Intl.NumberFormat(userLocale, {
            style: 'currency',
            currency,
        })
    }

    return numberFormat.format(Big(number).toNumber())
}

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

/**
 * This function is copied from lune-backend
 * https://github.com/lune-climate/lune-backend/blob/176ec4416c7e6c902ce0f3200c4da9e895c90ab4/src/utils.ts#L1341
 *
 * Gets a stable JSON representation of a value.
 *
 * Stable in this context means that object properties are always returned in the same order,
 * regardless of the order in which they were defined.
 *
 * Useful for when you want to use JSON to compare two values for equality but the values
 * are objects or contain objects and you can't depend on the objects' property ordering.
 *
 * @example
 *
 * stableJson({a: 1, b: 2}) === stableJson({b: 2, a: 1}) // true
 */
export function stableJson(value: unknown): string {
    function replacer(_key: string, value: unknown): unknown {
        if (typeof value !== 'object' || value === null) {
            return value
        }
        const entries = Object.entries(value).sort(([aName, _aValue], [bName, _bValue]) =>
            aName.localeCompare(bName),
        )
        return Object.fromEntries(entries)
    }
    return JSON.stringify(value, replacer, 0)
}

/**
 * Converts a builtin {@link Date} to a {@link LocalDate} discarding any time components
 * regardless of their value.
 *
 * @example
 * ```typescript
 * truncateDateToLocalDate(new Date('2020-10-20T10:20:30Z')) // LocalDate.of(2020, 10, 20)
 * ```
 */
export function truncateDateToLocalDate(date: Date): LocalDate {
    // Yes, Date's getMonth returns a zero-based month number while LocalDate operates
    // on one-based month numbers.
    return LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate())
}
